import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useMutation, useQuery } from 'urql';

import {
  Card,
  CardCloseButton,
  CardOptions,
  CheckboxField,
  ErrorMessage,
  Form,
  SubmitButton,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import {
  ChangePlayerMarketingWebPushMutation,
  ChangePlayerMarketingWebPushMutationVariables,
  PlayerMarketingWebPushFormQuery,
} from './__generated__/PlayerMarketingWebPushForm';

const query = graphql`
  query SanityPlayerMarketingWebPushForm {
    sanityPlayerMarketingWebPushForm {
      title {
        ...SanityLocaleString
      }
    }
  }
`;

const playerQuery = gql`
  query PlayerMarketingWebPushForm($playerId: ID!) {
    player(playerId: $playerId) {
      id
      isWebPushMarketingAccepted
    }
  }
`;

const playerMutation = gql`
  mutation ChangePlayerMarketingWebPush(
    $playerId: ID!
    $isWebPushMarketingAccepted: Boolean!
  ) {
    changeWebPushMarketingSettings(
      playerId: $playerId
      isWebPushMarketingAccepted: $isWebPushMarketingAccepted
    ) {
      id
      isWebPushMarketingAccepted
    }
  }
`;

const PlayerMarketingWebPushForm: FC<{ playerId: string }> = ({ playerId }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerMarketingWebPushFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();

  const form = staticData.sanityPlayerMarketingWebPushForm;

  const [{ data }] = useQuery<PlayerMarketingWebPushFormQuery>({
    query: playerQuery,
    variables: { playerId },
  });

  const [changePlayerMarketingWebPushState, changePlayerMarketingWebPush] =
    useMutation<
      ChangePlayerMarketingWebPushMutation,
      ChangePlayerMarketingWebPushMutationVariables
    >(playerMutation);

  const defaultValues = {
    isWebPushMarketingAccepted: !!data?.player?.isWebPushMarketingAccepted,
  };

  const { close } = useModalContext();

  const { t } = useTranslate();

  const onSubmit = (values: typeof defaultValues) => {
    changePlayerMarketingWebPush({
      playerId,
      isWebPushMarketingAccepted: values.isWebPushMarketingAccepted,
    }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        close();
      }
    });
  };

  if (!form || !data) {
    return null;
  }

  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="p-3">
        <Form
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          className="grid grid-cols-2 sm:grid-cols-3 gap-6"
        >
          <CheckboxField
            title="Accepts Web Push Marketing"
            name="isWebPushMarketingAccepted"
            id="PlayerMarketingWebPushForm__isWebPushMarketingAccepted"
            className="col-span-full"
          />

          <ErrorMessage message={errorMessage} />
          <SubmitButton
            value="Submit"
            disabled={changePlayerMarketingWebPushState.fetching}
          />
        </Form>
      </div>
    </Card>
  );
};

export default PlayerMarketingWebPushForm;
